import * as React from "react";
import * as AlertDialogPrimitive from "@radix-ui/react-alert-dialog";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";

import { FOCUS_RING_INSET } from "../lib/focus";
import { cn } from "../lib/utils";
import { OVERLAY_FADE } from "../lib/overlay";
import { Button } from "./button";
import { IconButton } from "./icon-button";

/**
 * v2 Dialog and ConfirmDialog (redesign phase 5, #455). Storybook only until
 * implementation; see docs/design-library/04-implementation-notes.md. Same
 * part names as v1 `components/ui/dialog.tsx`, plus `DialogBody`.
 *
 * Structure: the content sits INSIDE the overlay (Radix's scrollable-overlay
 * pattern). The overlay is a centring grid with a 16 / 32px margin, so the
 * dialog's `max-h-full` is the viewport minus that margin without any vh
 * class. Clicks on the overlay are still outside the content and close it.
 *
 * Surface: `--popover`, 1px `--border`, radius 12, `shadow-dialog`, on
 * `--scrim` (navy 40% / black 60%, was black 80%; break #82, #83). Fade only.
 *
 * Layout: header (title 18/26, description Inter 14) and footer stay put, the
 * body scrolls between them; a `--divider` hairline shows at whichever body
 * edge has content scrolled under it (break #85). Padding 24 at the sides.
 * The close button is last in the DOM, so opening focuses the first field or
 * button in the dialog, not the X.
 *
 * Forms: wrap body and footer in `<form className="contents">` (the form must
 * not break the flex column), main action `type="submit"` and always enabled;
 * validate on submit and focus the first invalid field. Pass
 * `onInteractOutside={(e) => e.preventDefault()}` so a stray click outside
 * doesn't throw away input; Escape and Abbrechen still close (break #88).
 */

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

/** Scrim + centring grid; the margin keeps a dialog off the viewport edge. */
// One `minmax(0, 1fr)` row and column: an auto track would grow with a long dialog, and max-h-full would never bind.
export const DIALOG_OVERLAY = `fixed inset-0 z-50 grid grid-cols-1 grid-rows-1 place-items-center overflow-hidden bg-scrim p-4 sm:p-8 ${OVERLAY_FADE}`;

/** The shared panel look of Dialog, ConfirmDialog and Sheet. */
export const DIALOG_SURFACE =
  "relative flex w-full max-h-full flex-col border border-border bg-popover text-popover-foreground shadow-dialog outline-hidden pb-5";

export type DialogSize = "sm" | "md" | "lg" | "xl";

const SIZE: Record<DialogSize, string> = {
  sm: "max-w-100",
  md: "max-w-130",
  lg: "max-w-180",
  // Fixed large: documents, conversations. Fills the available height, so the content doesn't jump.
  xl: "h-full max-w-5xl",
};

export interface DialogContentProps extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  /** sm 400, md 520 (default), lg 720, xl 1024 and full available height. */
  size?: DialogSize;
  /** Accessible name of the X. German UI text. */
  closeLabel?: string;
  /** No X: the dialog must be answered through its footer. */
  hideClose?: boolean;
}

const DialogContent = React.forwardRef<React.ElementRef<typeof DialogPrimitive.Content>, DialogContentProps>(
  function DialogContent({ size = "md", closeLabel = "Schließen", hideClose = false, className, children, ...props }, ref) {
    return (
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className={DIALOG_OVERLAY}>
          <DialogPrimitive.Content
            ref={ref}
            className={cn(DIALOG_SURFACE, "rounded-xl", SIZE[size], className)}
            {...props}
          >
            {children}
            {!hideClose && <DialogCloseButton label={closeLabel} />}
          </DialogPrimitive.Content>
        </DialogPrimitive.Overlay>
      </DialogPrimitive.Portal>
    );
  },
);

/** The X, top right, aligned with the title line. Shared with Sheet. */
export function DialogCloseButton({ label }: { label: string }) {
  return (
    <DialogPrimitive.Close asChild>
      <IconButton icon={X} label={label} size="sm" className="absolute top-5.5 right-5" />
    </DialogPrimitive.Close>
  );
}

/** Title and description. Leaves room on the right for the X. */
function DialogHeader({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return <div className={cn("flex shrink-0 flex-col gap-1 px-6 pt-6 pb-4 pr-14", className)} {...props} />;
}

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(function DialogTitle({ className, ...props }, ref) {
  return <DialogPrimitive.Title ref={ref} className={cn("font-sans text-heading-md text-ink", className)} {...props} />;
});

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(function DialogDescription({ className, ...props }, ref) {
  return (
    <DialogPrimitive.Description ref={ref} className={cn("font-body text-body text-ink-muted", className)} {...props} />
  );
});

/**
 * Which edges of a scroller have content beyond them. Re-measured on scroll and
 * whenever the scroller or its content changes size.
 */
function useScrollEdges(scroller: React.RefObject<HTMLElement | null>, content: React.RefObject<HTMLElement | null>) {
  const [edges, setEdges] = React.useState({ top: false, bottom: false });
  React.useLayoutEffect(() => {
    const el = scroller.current;
    if (!el) return;
    const update = () => {
      const top = el.scrollTop > 0;
      const bottom = el.scrollTop + el.clientHeight < el.scrollHeight - 1;
      setEdges((prev) => (prev.top === top && prev.bottom === bottom ? prev : { top, bottom }));
    };
    update();
    el.addEventListener("scroll", update, { passive: true });
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(update);
    observer?.observe(el);
    if (content.current) observer?.observe(content.current);
    return () => {
      el.removeEventListener("scroll", update);
      observer?.disconnect();
    };
  }, [scroller, content]);
  return edges;
}

/**
 * The scrolling middle. Children stack with 16px gaps (field to field); a
 * `flex-1` child fills the body (xl document views). While it overflows it is
 * focusable, so keyboard users can scroll a body without fields.
 */
function DialogBody({ className, children, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  const scroller = React.useRef<HTMLDivElement>(null);
  const content = React.useRef<HTMLDivElement>(null);
  const { top, bottom } = useScrollEdges(scroller, content);
  const overflowing = top || bottom;
  return (
    <div
      ref={scroller}
      data-overflow={overflowing || undefined}
      tabIndex={overflowing ? 0 : undefined}
      className={cn(
        "min-h-0 flex-1 overflow-y-auto border-y border-transparent px-6 py-1 outline-hidden",
        FOCUS_RING_INSET,
        top && "border-t-divider",
        bottom && "border-b-divider",
      )}
      {...props}
    >
      <div ref={content} className={cn("flex min-h-full flex-col gap-4 font-body text-body text-popover-foreground", className)}>
        {children}
      </div>
    </div>
  );
}

/**
 * Whether a footer's actions no longer fit on one row (break #174). Measured,
 * not a breakpoint: two short actions stay side by side in a 400 dialog, two
 * long German ones stack in a 375 Sheet. Runs in a layout effect, so the first
 * paint already has the right layout.
 *
 * In a row the actions keep their natural width, so their sum (plus gaps and
 * padding) is what the row needs; `scrollWidth` can't say it, `justify-end`
 * overflows to the left. Stacked, the actions stretch and say nothing about
 * their natural width, so the footer unstacks only once it is as wide as the
 * row needed when it stacked (no flip-flop at the edge).
 */
function useStackedActions(ref: React.RefObject<HTMLDivElement | null>) {
  const [stacked, setStacked] = React.useState(false);
  const stackedRef = React.useRef(false);
  const needed = React.useRef(0);

  React.useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const set = (next: boolean) => {
      stackedRef.current = next;
      setStacked(next);
    };
    const measure = () => {
      const width = el.clientWidth;
      if (width === 0) return; // not laid out (hidden, jsdom)
      if (stackedRef.current) {
        if (width >= needed.current) set(false);
        return;
      }
      const style = getComputedStyle(el);
      const children = Array.from(el.children) as HTMLElement[];
      const gap = parseFloat(style.columnGap) || 0;
      const row =
        children.reduce((sum, child) => sum + child.getBoundingClientRect().width, 0) +
        gap * Math.max(children.length - 1, 0) +
        (parseFloat(style.paddingLeft) || 0) +
        (parseFloat(style.paddingRight) || 0);
      if (row > width + 0.5) {
        needed.current = row;
        set(true);
      }
    };
    measure();
    if (typeof ResizeObserver === "undefined") return;
    // The footer for the dialog's width, the actions for label changes (loading, a longer verb).
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    for (const child of Array.from(el.children)) observer.observe(child);
    return () => observer.disconnect();
  }, [ref]);

  return stacked;
}

/**
 * Actions right: Abbrechen (outline) first, the main action last. When they
 * don't fit on one row they stack full width, the main action on top
 * (`flex-col-reverse`: the DOM and tab order stay Abbrechen → main action).
 */
function DialogFooter({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  const ref = React.useRef<HTMLDivElement>(null);
  const stacked = useStackedActions(ref);
  return (
    <div
      ref={ref}
      data-stacked={stacked || undefined}
      className={cn(
        "flex shrink-0 items-center justify-end gap-2 px-6 pt-4 pb-1",
        stacked && "flex-col-reverse items-stretch",
        className,
      )}
      {...props}
    />
  );
}

// ── ConfirmDialog ─────────────────────────────────────────────────────────────

export interface ConfirmDialogProps {
  /** A question: "Maschine löschen?" */
  title: React.ReactNode;
  /** What happens, and whether it can be undone. */
  description: React.ReactNode;
  /** The verb of the action: "Löschen". */
  confirmLabel: string;
  cancelLabel?: string;
  /** `destructive` (default): filled red main action. `default`: the blue one. */
  variant?: "destructive" | "default";
  /**
   * Runs on confirm. Return a promise to keep the dialog open with a loading
   * button until it settles: it closes on resolve and stays open on reject
   * (report the error inside onConfirm).
   */
  onConfirm: () => void | Promise<unknown>;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Rendered as the AlertDialog trigger (asChild). */
  trigger?: React.ReactElement;
  /** Extra body content between description and actions. */
  children?: React.ReactNode;
}

/**
 * The AlertDialog variant of Dialog (break #87): `role="alertdialog"`, no X,
 * outside clicks ignored, focus starts on Abbrechen, so Enter right after
 * opening does not destroy anything. Always size sm.
 */
function ConfirmDialog({
  title,
  description,
  confirmLabel,
  cancelLabel = "Abbrechen",
  variant = "destructive",
  onConfirm,
  open: openProp,
  defaultOpen = false,
  onOpenChange,
  trigger,
  children,
}: ConfirmDialogProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultOpen);
  const controlled = openProp !== undefined;
  const open = controlled ? openProp : uncontrolledOpen;
  const [pending, setPending] = React.useState(false);

  const setOpen = (next: boolean) => {
    // While the action runs there is nothing to cancel: Escape and Abbrechen wait.
    if (!next && pending) return;
    if (!controlled) setUncontrolledOpen(next);
    onOpenChange?.(next);
  };

  const confirm = async () => {
    const result = onConfirm();
    if (!(result instanceof Promise)) {
      setOpen(false);
      return;
    }
    setPending(true);
    try {
      await result;
    } catch {
      setPending(false);
      return;
    }
    setPending(false);
    if (!controlled) setUncontrolledOpen(false);
    onOpenChange?.(false);
  };

  return (
    <AlertDialogPrimitive.Root open={open} onOpenChange={setOpen}>
      {trigger && <AlertDialogPrimitive.Trigger asChild>{trigger}</AlertDialogPrimitive.Trigger>}
      <AlertDialogPrimitive.Portal>
        <AlertDialogPrimitive.Overlay className={DIALOG_OVERLAY}>
          <AlertDialogPrimitive.Content className={cn(DIALOG_SURFACE, "rounded-xl", SIZE.sm)}>
            <DialogHeader className="pr-6">
              <AlertDialogPrimitive.Title className="font-sans text-heading-md text-ink">{title}</AlertDialogPrimitive.Title>
              <AlertDialogPrimitive.Description className="font-body text-body text-ink-muted">
                {description}
              </AlertDialogPrimitive.Description>
            </DialogHeader>
            {children != null && <DialogBody>{children}</DialogBody>}
            <DialogFooter>
              <AlertDialogPrimitive.Cancel asChild>
                <Button variant="outline" disabled={pending}>
                  {cancelLabel}
                </Button>
              </AlertDialogPrimitive.Cancel>
              <Button variant={variant} loading={pending} onClick={() => void confirm()}>
                {confirmLabel}
              </Button>
            </DialogFooter>
          </AlertDialogPrimitive.Content>
        </AlertDialogPrimitive.Overlay>
      </AlertDialogPrimitive.Portal>
    </AlertDialogPrimitive.Root>
  );
}

export {
  Dialog,
  DialogPortal,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  ConfirmDialog,
};
